import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";
import type { ValueBasis } from "./concentration";

/**
 * The total value and contract count of the book a Monitor selection names (`GET /portfolio-total`): one investor or all, one facility or
 * all, Loans / Leases / both, on the chosen "Measured in" basis. Read from the same investor slice the Monitor's own rows are measured
 * against, so the cards and the limits table cannot disagree.
 */
export interface PortfolioTotal {
  /** the snapshot the figures are at: the latest on or before the date asked for */
  asOfDate: string | null;
  investorName: string;
  facility: string;
  product: string;
  valueBasis: ValueBasis;
  /** null (not 0): the basis is not populated for the date */
  value: number | null;
  contractCount: number;
  /** why the figures are 0, when the investor has no book attributed to it */
  note: string | null;
}

export function usePortfolioTotal(opts: {
  investor: string;
  /** "ALL" or a facility code */
  facility: string;
  /** "ALL" | "LOAN" | "LEASE" */
  product: string;
  valueBasis: ValueBasis;
  /** yyyy-MM-dd */
  asOfDate: string;
}) {
  const { investor, facility, product, valueBasis, asOfDate } = opts;
  return useQuery({
    queryKey: ["prc-portfolio-total", investor, facility, product, valueBasis, asOfDate],
    queryFn: () => {
      // valueBasis is always sent: the API's own default is net investment, the page's is present value
      const params = new URLSearchParams({ investor, as_of: asOfDate, valueBasis });
      if (facility && facility !== "ALL") params.set("facility", facility);
      if (product && product !== "ALL") params.set("product", product);
      return getJson<PortfolioTotal>(`${PRC_PREFIX}portfolio-total?${params.toString()}`);
    },
    // switching a filter keeps the last figures on screen, dimmed by the caller, instead of blanking the cards
    placeholderData: keepPreviousData,
  });
}
